import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
    advanceLiveSession,
    endLiveSession,
    getPresenterState,
    revealLiveSession,
} from "../../services/liveSessionApi";

// Presenter/projector view (Q7-T1 §4.3): full-screen current question, a large
// scannable join QR + short code, live answer count, aggregate bars on REVEAL, and
// next/reveal/end controls. Polls the director state endpoint ~2s with the
// stateVersion short-circuit.
const POLL_MS = 2000;

export default function LivePresenter() {
    const { sessionId } = useParams();
    const [view, setView] = useState(null);
    const [error, setError] = useState("");
    const versionRef = useRef(null);

    const poll = useCallback(async () => {
        try {
            const next = await getPresenterState(sessionId);
            if (next && next.stateVersion !== versionRef.current) {
                versionRef.current = next.stateVersion;
            }
            setView(next);
            setError("");
        } catch (e) {
            setError(e && e.status === 404 ? "Session not available" : "Connection issue — retrying");
        }
    }, [sessionId]);

    useEffect(() => {
        poll();
        const id = setInterval(poll, POLL_MS);
        return () => clearInterval(id);
    }, [poll]);

    const doAction = async (fn) => {
        try {
            await fn(sessionId);
            await poll();
        } catch (e) {
            setError(e && e.message ? e.message : "Action failed");
        }
    };

    if (!view) {
        return <div style={{ padding: 24 }}>{error || "Loading…"}</div>;
    }

    const joinUrl = `${window.location.origin}/live/${view.shortCode}`;
    const total = view.aggregateCounts ? Object.values(view.aggregateCounts).reduce((a, b) => a + b, 0) : 0;

    return (
        <div style={{ padding: 24, fontFamily: "system-ui, sans-serif" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                    <div style={{ fontSize: 18, color: "#666" }}>Join at <strong>{joinUrl}</strong></div>
                    <div style={{ fontSize: 64, fontWeight: 800, letterSpacing: 6 }}>{view.shortCode}</div>
                    <div style={{ fontSize: 18 }}>Joined: <strong>{view.joinCount}</strong></div>
                </div>
                <QRCodeSVG value={joinUrl} size={220} />
            </div>

            {view.state === "LOBBY" && <h2 style={{ marginTop: 24 }}>Waiting for the room to fill…</h2>}

            {view.question && (
                <div style={{ marginTop: 24 }}>
                    <h1>{view.question.stem}</h1>
                    <div style={{ color: "#666" }}>
                        Answered: {view.answeredCount} / {view.joinCount}
                    </div>
                    <ul style={{ listStyle: "none", padding: 0 }}>
                        {(view.question.options || []).map((opt) => {
                            const count = (view.aggregateCounts && view.aggregateCounts[opt]) || 0;
                            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                            const isCorrect = view.state === "REVEAL" && view.correctOption === opt;
                            return (
                                <li key={opt} style={{ margin: "8px 0" }}>
                                    <div style={{ fontWeight: isCorrect ? 700 : 400 }}>
                                        {opt} {isCorrect ? "✓" : ""}
                                    </div>
                                    {view.state === "REVEAL" && (
                                        <div style={{ background: "#eee", height: 20, borderRadius: 4 }}>
                                            <div style={{ width: `${pct}%`, background: isCorrect ? "#137333" : "#4285f4", height: 20, borderRadius: 4 }} />
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
                {(view.state === "LOBBY" || view.state === "REVEAL") && (
                    <button onClick={() => doAction(advanceLiveSession)} style={{ padding: "10px 18px" }}>Next question</button>
                )}
                {view.state === "QUESTION" && (
                    <button onClick={() => doAction(revealLiveSession)} style={{ padding: "10px 18px" }}>Reveal</button>
                )}
                {view.state !== "ENDED" && (
                    <button onClick={() => doAction(endLiveSession)} style={{ padding: "10px 18px" }}>End session</button>
                )}
                {view.state === "ENDED" && <span>Session ended.</span>}
            </div>
            {error && <p style={{ color: "#b06000" }}>{error}</p>}
        </div>
    );
}
